import React, { useState, useEffect, useMemo, useContext } from "react"
import styled from "styled-components"

import { useMedia } from "utils/hooks"

import colors from "assets/styles/colors"
import text from "assets/styles/text"
import media from "assets/styles/media"
import easing from "assets/styles/easings"

import BGGrid from "components/uIElements/BGGrid"
import arrow from "assets/images/global/fatArrow.svg"

import news1 from "assets/images/homepage/news1.webp"
import news2 from "assets/images/homepage/news2.webp"
import news3 from "assets/images/homepage/news3.webp"
import { ScreenContext } from "components/Providers"
import PixelButton from "components/buttons/PixelButton"

const newsArray = [
  {
    date: "April 15 2021",
    headline: "Looking Back on Olo",
    img: news1,
    link: "https://blog.rre.com/looking-back-on-olo-60d24a930f3e",
  },
  {
    date: "April 15 2021",
    headline: "Spire and BlackSky to go public via SPAC mergers",
    img: news2,
    link: "https://blog.rre.com/spire-and-blacksky-to-go-public-via-spac-mergers-b5bd052fc48e",
  },
  {
    date: "April 15 2021",
    headline: "Latch to go public via SPAC, Merging with Tishman Speyer’s TSIA",
    img: news3,
    link: "https://commercialobserver.com/2021/06/latch-goes-public-tishman-speyer-spac/",
  },
]

const Quotes: React.FC<{}> = () => {
  const [active, setActive] = useState<number>(0)
  const [pos, setPos] = useState<number>(0)
  const { mobile } = useContext(ScreenContext)
  const multiplier = useMedia(37.92, 37.92, 55.66, 0) ?? 37.92
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null)

  const allNews = useMemo(
    () =>
      newsArray.map((news, i) => {
        return (
          <NewsItem
            key={`Home_NewsItem-${i}`}
            href={news.link}
            target="__blank"
          >
            <Content>
              <NewsImage src={news.img} alt={news.headline} />
              <TextContent>
                <NewsHeadline>{news.headline}</NewsHeadline>
                <Date>{news.date}</Date>
              </TextContent>
            </Content>
          </NewsItem>
        )
      }),
    [newsArray]
  )

  const handleNext = () => {
    if (active < newsArray.length - 1) {
      setActive(active + 1)
    }
  }

  const handlePrevious = () => {
    if (active > 0) {
      setActive(active - 1)
    }
  }

  const lineConfig = {
    sectionHeight: "100%",
    sectionRef: wrapper,
    animationClass: "news",
  }

  useEffect(() => {
    setPos(multiplier * active)
  }, [active])

  return (
    <Wrapper
      ref={ref => setWrapper(ref)}
      id="NewsFeed"
      data-scroll
      data-scroll-section
    >
      <BGGrid lineConfig={lineConfig} />
      <Headline>
        News Feed <span>(0{allNews.length})</span>
      </Headline>
      {!mobile && (
        <Controls>
          <PixelButton
            left={true}
            onClick={handlePrevious}
            disabled={active === 0}
            pixelsToChange={[
              { from: [7, 0], to: [7, -1] },
              { from: [8, 1], to: [8, -1] },
              { from: [9, 0], to: [9, -2] },
            ]}
            color={colors.white}
            icon={
              <img src={arrow} alt={"left arrow"} height="50%" width="50%" />
            }
          />
          <PixelButton
            disabled={active === newsArray.length - 1}
            onClick={handleNext}
            color={colors.white}
            icon={
              <img src={arrow} alt={"right arrow"} height="50%" width="50%" />
            }
          />
        </Controls>
      )}
      <Carousel>
        <Inner animationPos={pos}>{allNews}</Inner>
      </Carousel>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  background: ${colors.blue400};
  height: 86.11vw;
  padding: 17.36vw 0 32.57vw 12.99vw;

  ${media.tablet} {
    height: 150.39vw;
    padding: 24.41vw 0 43.95vw 4.88vw;
    display: grid;
  }

  ${media.mobile} {
    position: relative;
    height: 370.07vw;
    padding: 26.667vw 4vw 40vw;
  }
`

const Carousel = styled.div`
  position: relative;
  width: 87.01vw;
  height: 31.94vw;

  ${media.tablet} {
    width: 100%;
    height: 44.92vw;
  }
  ${media.mobile} {
    width: 100%;
    height: auto;
  }
`

const Inner = styled.div<{ animationPos: number }>`
  position: absolute;
  display: flex;
  transform: translateX(-${props => props.animationPos}vw);
  transition: 0.5s;
  ${media.tablet} {
  }
  ${media.mobile} {
    position: relative;
    flex-direction: column;
    transform: translateX(0);
  }
`

const Headline = styled.h3`
  ${text.desktop.h3};
  color: ${colors.white};
  position: relative;
  width: fit-content;
  span {
    position: absolute;
    top: 0;
    right: -12%;
    ${text.desktop.d4};
  }
  margin-bottom: 1.39vw;
  ${media.tablet} {
    ${text.tablet.h3};
    margin-bottom: 7.81vw;
    span {
      ${text.tablet.d4};
      right: -11%;
    }
  }
  ${media.mobile} {
    ${text.mobile.h4};
    margin-bottom: 10.67vw;
    span {
      position: absolute;
      top: 0;
      right: -15%;
      ${text.mobile.d5};
    }
  }
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  left: 0;
  top: 0;
  z-index: 100;
  margin-bottom: 5.56vw;
  ${media.tablet} {
    margin-bottom: 0;
    margin-top: 9.77vw;
    order: 1;
  }
  ${media.mobile} {
  }
`
const Content = styled.div`
  background: white;
  width: 100%;
  height: 100%;
  transition: 0.4s;
  transition-timing-function: ${easing.main};
`

const TextContent = styled.div`
  padding: 2.78vw;
  ${media.tablet} {
    padding: 3.91vw;
  }
  ${media.mobile} {
    padding: 8vw;
  }
`

const NewsHeadline = styled.p`
  ${text.desktop.h5};
  color: ${colors.black800};
  height: 7.5vw;
  ${media.tablet} {
    ${text.tablet.h5};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    height: 10.55vw;
  }
  ${media.mobile} {
    ${text.mobile.h0Point5};
    height: 16vw;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: 2.67vw;
  }
`
const Date = styled.p`
  ${text.desktop.d5};
  color: ${colors.blue400};

  ${media.tablet} {
    ${text.tablet.d5};
  }
  ${media.mobile} {
    ${text.mobile.d5};
  }
`

const NewsItem = styled.a`
  width: 33.33vw;
  height: 31.94vw;
  position: relative;
  margin-right: 4.58vw;
  cursor: pointer;
  background: ${colors.black800};
  ${media.hover} {
    :hover {
      ${Content} {
        transform: translate(1.04vw, -1.04vw);
      }
    }
  }

  ${media.tablet} {
    width: 46.88vw;
    height: 44.92vw;
    margin-right: 8.69vw;
  }
  ${media.mobile} {
    width: 100%;
    height: 88.27vw;
    margin-right: 0;
    margin-bottom: 5.33vw;
  }
`

const NewsImage = styled.img`
  width: 100%;
  position: relative;
  height: auto;
  object-fit: cover;

  ${media.tablet} {
  }
  ${media.mobile} {
  }
`

export default Quotes
